import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SortIcon from "@mui/icons-material/Sort";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const sortOptions = [
  { value: "-popular", label: "محبوب‌ترین" },
  { value: "-created_at", label: "جدیدترین" },
  { value: "price", label: "ارزان‌ترین" },
  { value: "-price", label: "گران‌ترین" },
];

export default function ProductSortbar({
  activeFilters,
  onFilterChange,
  onOpenMobileFilters,
  showDesktopFilters,
  onToggleDesktopFilters,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const currentSort = activeFilters.ordering || "-popular";
  const selectedOption =
    sortOptions.find((opt) => opt.value === currentSort) || sortOptions[0];

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleSelectOption = (value) => {
    onFilterChange({ ordering: value });
    handleCloseMenu();
  };

  return (
    <Box
      sx={{
        display: "flex",
        // On mobile, keep space-between; on desktop, align everything to the start (right side in RTL)
        justifyContent: isMobile ? "space-between" : "flex-start",
        alignItems: "center",
        gap: { xs: 1, md: 2 },
        bgcolor: "#fff",
        px: { xs: 1.5, md: 2.5 },
        py: 1.2,
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
      }}
    >
      {/* 1. Mobile Filter Button */}
      {isMobile && (
        <Button
          onClick={onOpenMobileFilters}
          startIcon={<FilterAltOutlinedIcon sx={{ ml: 0.5, mr: -0.5 }} />}
          sx={{
            color: "grey.800",
            fontWeight: 600,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 2,
            px: 1.8,
            py: 0.6,
            fontSize: "0.85rem",
          }}
        >
          فیلترها
        </Button>
      )}

      {/* 2. Desktop Show Filters Toggle Button */}
      {!isMobile && !showDesktopFilters && (
        <>
          <Button
            onClick={onToggleDesktopFilters}
            startIcon={<FilterAltOutlinedIcon sx={{ ml: 0.5, mr: -0.5 }} />}
            sx={{
              color: "grey.800",
              fontWeight: 600,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 2,
              px: 1.8,
              py: 0.6,
              fontSize: "0.85rem",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            نمایش فیلترها
          </Button>

          {/* Divider between Filter Button & Sort Label */}
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        </>
      )}

      {/* 3. Sort Label & Dropdown Group (Placed directly next to the filter button) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {!isMobile && (
          <Box
            sx={{ display: "flex", alignItems: "center", color: "grey.700" }}
          >
            <SortIcon fontSize="small" sx={{ ml: 0.5 }} />
            <Typography variant="body2" fontWeight={700}>
              مرتب‌سازی:
            </Typography>
          </Box>
        )}

        <Button
          onClick={handleOpenMenu}
          endIcon={
            <KeyboardArrowDownIcon
              sx={{
                mr: 0.5,
                ml: -0.5,
                transition: "transform 0.2s",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          }
          sx={{
            color: "#ef4056",
            fontWeight: 700,
            fontSize: "0.85rem",
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 2,
            px: 2,
            py: 0.6,
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          {selectedOption.label}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 160,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              border: "1px solid",
              borderColor: "grey.200",
            },
          }}
        >
          {sortOptions.map((option) => {
            const isSelected = option.value === currentSort;
            return (
              <MenuItem
                key={option.value}
                selected={isSelected}
                onClick={() => handleSelectOption(option.value)}
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? "#ef4056" : "text.primary",
                  py: 1,
                  px: 2,
                  "&.Mui-selected": {
                    bgcolor: "rgba(239, 64, 86, 0.08)",
                  },
                }}
              >
                {option.label}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    </Box>
  );
}